import type Luminary from '../';
import { assertObject } from '../validators';
import type { MixinBase, MergeConstructors, GetConstructorParameters } from '../types';
import type { Template } from './template/Template';
import type { Slide } from './slide/Slide';
import type { Container } from './asset/Container';
import type { SerializableMixinSettings } from './serializableMixin';

export type ParentMixinSettings = {
    /**
     * the parent object
     */
    parent?: Object;
} & Partial<SerializableMixinSettings>;

export interface ParentMixinInterface {
    new(): typeof ParentMixin;
    _classType: string;
    get parent(): unknown;
    get container(): Container;
    get slide(): Slide;
    get root(): Luminary;
    get template(): Template;
};

/**
 * This Mixin provides a `parent` if one is required.
 *
 * Some Mixins require access to a `parent`. If the class we are mixing new behaviour into does not already provide a
 * `parent`, this Mixin is will provide a read-only parent.
 * @deprecated Use TypedParentMixin
 */
export const ParentMixin = <TBase extends MixinBase>(Base: TBase) => {
    const Derived = class extends (Base as any) {
        _classType = 'ParentFlowMixin';

        #parent = null;

        constructor(settings: ParentMixinSettings = {}) {
            // eslint-disable-next-line constructor-super
            super(settings);

            if (settings.hasOwnProperty('parent')) {
                assertObject(settings.parent);
                this.#parent = settings.parent;
            }
        }

        /**
         * Our parent object.
         */
        get parent() {
            return this.#parent;
        }

        get container() {
            return this.parent?.container;
        }

        get slide() {
            return this.parent?.slide;
        }

        get root() {
            return this.parent?.root;
        }

        get template(): Template {
            return this.parent?.slide?.template || this.parent?.template;
        }
    };

    return Derived as MergeConstructors<typeof Derived, TBase>;
};

export const TypedParentMixin = <TBase>(Base: MixinBase<TBase>) => {
    const Derived = ParentMixin(Base as any);

    type ReturnType = ParentMixinInterface & TBase;

    return Derived as unknown as new (props: GetConstructorParameters<typeof Derived>) => ReturnType;
};
